import { useMutation, useQueryClient } from "@tanstack/vue-query";
import type { TodoStatus, TodoSubmission } from "@/components/MyStudentDetail/FilterableTodosSection/types";
import profileService from "@/services/profile.js";
import { getTodosSubmissionsQueryKey } from "./useGetTodosSubmissions";

export type UpdateTodoStatusArgs = {
  participantId: string;
  applicationId: string;
  todoId: string;
  status: TodoStatus;
};

type MutationContext = {
  previousTodos: TodoSubmission[] | undefined;
  participantId: string;
};

export const useUpdateTodoStatus = () => {
  const queryClient = useQueryClient();

  const updateStatus = useMutation<void, Error, UpdateTodoStatusArgs, MutationContext>({
    mutationFn: async ({ applicationId, todoId, status }) => {
      await profileService.upsertTodoStatusOverride(applicationId, todoId, status);
    },
    onMutate: async ({ participantId, todoId, status }) => {
      const queryKey = getTodosSubmissionsQueryKey(participantId);
      await queryClient.cancelQueries({ queryKey });
      const previousTodos = queryClient.getQueryData<TodoSubmission[]>(queryKey);
      queryClient.setQueryData<TodoSubmission[]>(queryKey, (todos) =>
        todos?.map((todo) => (todo.todo_id === todoId ? { ...todo, status } : todo)),
      );
      return { previousTodos, participantId };
    },
    onError: (_error, _variables, context) => {
      if (!context) return;
      queryClient.setQueryData(getTodosSubmissionsQueryKey(context.participantId), context.previousTodos);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: getTodosSubmissionsQueryKey(variables.participantId) });
    },
  });

  return { updateStatus };
};
